import React, { useEffect, useId, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EyeIcon, EyeOff } from 'lucide-react';
import { Alert, Button, Form, Input, Label } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { isApiError, getFieldErrors } from '@/services/api';
import { useSmriqCredentialQuery } from '../hooks/useSmriqCredentialQuery';
import {
    useSmriqCredentialMutation,
    useSmriqCredentialDeleteMutation,
} from '../hooks/useSmriqCredentialMutation';
import {
    smriqCredentialSchema,
    type SmriqCredentialFormInput,
    type SmriqCredentialFormOutput,
} from '../schemas/smriqCredential.schema';

interface Props {
    productId: string;
}

const defaultValues: SmriqCredentialFormInput = { api_key: '', division: '' };

export const SmriqCredentialsPanel: React.FC<Props> = ({ productId }) => {
    const { data: status, isLoading: isStatusLoading, isError: isStatusError } = useSmriqCredentialQuery(productId);
    const mutation = useSmriqCredentialMutation(productId);
    const deleteMutation = useSmriqCredentialDeleteMutation(productId);
    const [showKey, setShowKey] = useState(false);
    const [saved, setSaved] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [confirmingRemove, setConfirmingRemove] = useState(false);
    const [removeError, setRemoveError] = useState<string | null>(null);
    // When a key is already stored the form is hidden behind "Edit Key"; it opens only on request.
    const [formOpen, setFormOpen] = useState(false);
    const formRegionId = useId();
    const editTriggerRef = useRef<HTMLDivElement>(null);
    const [restoreTriggerFocus, setRestoreTriggerFocus] = useState(false);

    const {
        control,
        handleSubmit,
        reset,
        setError,
        formState: { isValid },
    } = useForm<SmriqCredentialFormInput, unknown, SmriqCredentialFormOutput>({
        mode: 'onChange',
        resolver: zodResolver(smriqCredentialSchema),
        defaultValues,
    });

    // The key is write-only: every collapse clears form state and re-masks, so a draft never
    // survives in the hidden region, in memory, or reopens as plaintext.
    const collapseForm = () => {
        reset(defaultValues);
        setShowKey(false);
        setFormOpen(false);
        setRestoreTriggerFocus(true);
    };

    // Runs after commit, and waits out an in-flight mutation: a disabled trigger cannot take
    // focus, so focusing it before the mutation settles would silently drop it.
    useEffect(() => {
        if (!restoreTriggerFocus || mutation.isPending || deleteMutation.isPending) return;
        editTriggerRef.current?.querySelector('button')?.focus();
        setRestoreTriggerFocus(false);
    }, [restoreTriggerFocus, mutation.isPending, deleteMutation.isPending]);

    const onSubmit = (data: SmriqCredentialFormOutput) => {
        setSaved(false);
        setFormError(null);
        mutation.mutate(data, {
            onSuccess: () => {
                collapseForm();
                setSaved(true);
            },
            onError: (err) => {
                if (isApiError(err) && err.status === 422) {
                    const fields = getFieldErrors(err);
                    let mappedAnyField = false;
                    if (fields.api_key?.[0]) {
                        setError('api_key', { type: 'server', message: fields.api_key[0] });
                        mappedAnyField = true;
                    }
                    if (fields.division?.[0]) {
                        setError('division', { type: 'server', message: fields.division[0] });
                        mappedAnyField = true;
                    }
                    if (mappedAnyField) return;
                }
                // Fixed text, never the server message: an upstream error envelope that
                // echoed the submitted api_key would otherwise render it into the UI.
                setFormError('Failed to save SMRiQ credentials.');
            },
            // The mutation cache retains state.variables/state.error (plaintext api_key,
            // or a rejected request echoing it) until reset; clear it as soon as we settle.
            onSettled: () => mutation.reset(),
        });
    };

    const onRemove = () => {
        setRemoveError(null);
        deleteMutation.mutate(undefined, {
            onSuccess: () => {
                setConfirmingRemove(false);
                setSaved(false);
            },
            onError: () => setRemoveError('Failed to remove the SMRiQ key.'),
        });
    };

    if (isStatusLoading) return <p className="text-sm text-mp-muted">Loading SMRiQ credentials...</p>;

    const hasKey = status?.has_key ?? false;
    // No key yet: the form is the only way in, so it is always shown. With a key stored it is
    // hidden until "Edit Key" opens it.
    const formCollapsed = hasKey && !formOpen;
    // Replace and remove decide the same thing, so neither may start while the other is in
    // flight: whichever landed last would otherwise settle whether a credential exists.
    const credentialBusy = mutation.isPending || deleteMutation.isPending;

    return (
        <div className="space-y-6 max-w-md">
            <PermissionGuard permission={{ resource: 'smr', action: 'product.read' }}>
                <div>
                    <h3 className="font-semibold mb-2">Credential Status</h3>
                    {isStatusError ? (
                        <Alert variant="error">
                            Could not load the current SMRiQ credential status.
                        </Alert>
                    ) : (
                        <table className="w-full text-sm">
                            <tbody>
                                <tr className="border-b border-mp-border">
                                    <th className="w-32 px-3 py-2 text-left">API Key</th>
                                    <td className="px-3 py-2 text-left">
                                        {status?.has_key ? (
                                            `••••${status.last4 ?? ''}`
                                        ) : (
                                            <span className="text-mp-muted">Not configured</span>
                                        )}
                                    </td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Division</th>
                                    <td className="px-3 py-2 text-left">{status?.division ?? <span className="text-mp-muted">—</span>}</td>
                                </tr>
                                <tr className="border-b border-mp-border">
                                    <th className="px-3 py-2 text-left">Updated</th>
                                    <td className="px-3 py-2 text-left">
                                        {status?.updated_at ? (
                                            new Date(status.updated_at).toLocaleString()
                                        ) : (
                                            <span className="text-mp-muted">—</span>
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            </PermissionGuard>

            <PermissionGuard permission={{ resource: 'smr', action: 'product.edit' }}>
                {formError && (
                    <Alert variant="error">{formError}</Alert>
                )}
                {saved && (
                    <Alert variant="success" role="status">SMRiQ credentials saved.</Alert>
                )}

                {hasKey && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2">
                            {/* Stays mounted while collapsed so both states are announced and
                                aria-controls always resolves to a real element. */}
                            <div ref={editTriggerRef}>
                                <Button
                                    size="sm"
                                    variant="primary"
                                    type="button"
                                    onClick={() => (formOpen ? collapseForm() : setFormOpen(true))}
                                    ariaExpanded={formOpen}
                                    ariaControls={formRegionId}
                                    disabled={credentialBusy}
                                >
                                    Edit Key
                                </Button>
                            </div>
                            {!confirmingRemove && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    type="button"
                                    onClick={() => {
                                        setRemoveError(null);
                                        setConfirmingRemove(true);
                                    }}
                                    disabled={credentialBusy}
                                >
                                    Remove Key
                                </Button>
                            )}
                        </div>

                        {removeError && (
                            <Alert variant="error">{removeError}</Alert>
                        )}

                        {confirmingRemove && (
                            <div className="space-y-3">
                                <p className="text-sm">
                                    Remove the SMRiQ key from this product? It will no longer source
                                    services from SMRiQ until a new key is saved.
                                </p>
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        variant="danger"
                                        type="button"
                                        onClick={onRemove}
                                        disabled={credentialBusy}
                                    >
                                        {deleteMutation.isPending ? 'Removing...' : 'Confirm remove'}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        type="button"
                                        onClick={() => setConfirmingRemove(false)}
                                        disabled={credentialBusy}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div id={formRegionId} hidden={formCollapsed}>
                    <Form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        {hasKey && (
                            <p className="text-sm text-mp-muted">
                                Saving replaces the stored key in place, so the product keeps sourcing
                                from SMRiQ throughout. Remove it only to stop sourcing entirely.
                            </p>
                        )}

                        <div>
                            <Label htmlFor="smriq_api_key" required>API Key</Label>
                            <Controller
                                name="api_key"
                                control={control}
                                render={({ field, fieldState: { error: fe } }) => (
                                    <Input
                                        id="smriq_api_key"
                                        type={showKey ? 'text' : 'password'}
                                        placeholder="Enter the SMRiQ API key"
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={!!fe}
                                        hint={fe?.message}
                                        hintId="smriq_api_key-hint"
                                        aria-invalid={!!fe}
                                        aria-describedby={fe ? 'smriq_api_key-hint' : undefined}
                                        autoComplete="off"
                                    >
                                        <button
                                            type="button"
                                            onClick={() => setShowKey((v) => !v)}
                                            className="absolute z-30 -translate-y-1/2 right-3 top-1/2"
                                            aria-label={showKey ? 'Hide API key' : 'Show API key'}
                                        >
                                            {showKey ? (
                                                <EyeIcon size={18} className="text-mp-muted" />
                                            ) : (
                                                <EyeOff size={18} className="text-mp-muted" />
                                            )}
                                        </button>
                                    </Input>
                                )}
                            />
                        </div>

                        <div>
                            <Label htmlFor="smriq_division" required>Division</Label>
                            <Controller
                                name="division"
                                control={control}
                                render={({ field, fieldState: { error: fe } }) => (
                                    <Input
                                        id="smriq_division"
                                        type="text"
                                        placeholder="e.g. motorplan"
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={!!fe}
                                        hint={fe?.message}
                                        hintId="smriq_division-hint"
                                        aria-invalid={!!fe}
                                        aria-describedby={fe ? 'smriq_division-hint' : undefined}
                                    />
                                )}
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <Button size="sm" disabled={!isValid || credentialBusy}>
                                {mutation.isPending
                                    ? 'Saving...'
                                    : hasKey
                                      ? 'Replace Credentials'
                                      : 'Save Credentials'}
                            </Button>
                            {hasKey && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    type="button"
                                    onClick={collapseForm}
                                    disabled={credentialBusy}
                                >
                                    Cancel
                                </Button>
                            )}
                        </div>
                    </Form>
                </div>
            </PermissionGuard>
        </div>
    );
};
